/**
 * Options Lab — /options-lab/:symbol
 * Deep-dive research workbench for a single underlying.
 * Tabs: Overview | OI Analysis | IV & Skew | GEX | VWAP-OI | Option Chain | Futures
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Tabs, Tab, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, Chip, Grid, Button,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchLabData, analyzeSymbol, fetchBasket } from '../store/optionsSlice';
import { optionsService } from '../services/options.service';

import MarketStateStrip from '../components/options/MarketStateStrip';
import FreshnessBadge from '../components/common/FreshnessBadge';
import OIWallMap from '../components/options/OIWallMap';
import WhyThisSignalPanel from '../components/options/WhyThisSignalPanel';
import QuickBasketDrawer from '../components/options/QuickBasketDrawer';
import GreeksHistoryChart from '../components/options/GreeksHistoryChart';
import IVSmileChart from '../components/options/IVSmileChart';
import GEXChart from '../components/options/GEXChart';
import HVvsIVChart from '../components/options/HVvsIVChart';
import OptionChainTable from '../components/options/OptionChainTable';
import PayoffCurve from '../components/options/PayoffCurve';
import SignalCard from '../components/options/SignalCard';
import fnoService, { FuturesDerivedMetrics, FuturesSnapshot } from '../services/fno.service';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, ComposedChart, Area, Legend } from 'recharts';
import { useAppTheme } from '../hooks/useAppTheme';
import { AppColorTokens } from '../styles/colorTokens';

function isNseOpenFn(): boolean {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const h = ist.getHours(), m = ist.getMinutes();
  const total = h * 60 + m;
  const day = ist.getDay();
  if (day === 0 || day === 6) return false;
  return total >= 555 && total <= 930; // 9:15 to 15:30 IST
}

const OptionsLabPage: React.FC = () => {
  const { symbol = '' } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { C } = useAppTheme();

  const ivColor = (ivr: number) => ivr > 65 ? C.bear : ivr > 35 ? C.warn : C.bull;

  const [tab, setTab] = useState(0);
  const [expiries, setExpiries] = useState<any[]>([]);
  const [selectedExpiry, setSelectedExpiry] = useState<number | ''>('');
  const [ivHistory, setIvHistory] = useState<any[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [expiriesLoaded, setExpiriesLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [fetchingInitial, setFetchingInitial] = useState(false);

  // Futures tab state
  const [futMetrics, setFutMetrics] = useState<FuturesDerivedMetrics | null>(null);
  const [futSnapshots, setFutSnapshots] = useState<FuturesSnapshot[]>([]);
  const [futLoading, setFutLoading] = useState(false);

  const { labData, activeSignal, activeBasket, pulseCards } = useSelector((s: RootState) => s.options);
  const lab = labData[symbol.toUpperCase()];

  // Phase C — Quick-basket drawer state
  const [basketOpen, setBasketOpen] = useState(false);
  const underlyingCard = pulseCards.find(c => c.symbol.toUpperCase() === symbol.toUpperCase());
  const underlyingLot = underlyingCard?.lotSize || 50;

  // Lab period — own state, sync'd with localStorage shared with OptionsDashboardPage
  const [labDays, setLabDays] = useState<number>(() => {
    const saved = localStorage.getItem('fno_lab_days');
    return saved ? Math.max(1, Number(saved)) : 30;
  });
  const handleLabDaysChange = (v: number) => {
    setLabDays(v);
    localStorage.setItem('fno_lab_days', String(v));
  };

  // Load expiries — auto-select based on labDays
  useEffect(() => {
    if (!symbol) return;
    setExpiriesLoaded(false);
    optionsService.getExpiries(symbol).then(res => {
      const data = (res.data as any).data || [];
      setExpiries(data);
      if (data.length > 0) {
        // Default to the NEAREST active expiry — that's the one the poller
        // captures, so metrics render immediately. (Previously this defaulted to
        // an expiry "beyond the labDays cutoff" — often the monthly — which had
        // no captured metrics, leaving the Overview stuck on "Loading metrics…".)
        const now = new Date();
        const suited = data.filter((e: any) => new Date(e.expiryDate) >= now);
        setSelectedExpiry((suited[0] || data[0]).id);
      }
    }).catch(() => {}).finally(() => setExpiriesLoaded(true));
  }, [symbol]);

  // Load lab data when expiry or labDays changes — pass labDays so backend returns the right history window
  useEffect(() => {
    if (symbol && selectedExpiry) {
      dispatch(fetchLabData({ symbol: symbol.toUpperCase(), expiryId: Number(selectedExpiry), days: labDays }));
    }
  }, [symbol, selectedExpiry, labDays, dispatch]);

  // Load IV history for HV vs IV chart — respect labDays
  useEffect(() => {
    if (!symbol) return;
    optionsService.getIVHistory(symbol, labDays).then(res => {
      setIvHistory((res.data as any).data || []);
    }).catch(() => {});
  }, [symbol, labDays]);

  // Load futures data when Futures tab (tab 6) is activated
  useEffect(() => {
    if (tab !== 6 || !symbol) return;
    setFutLoading(true);
    Promise.all([
      fnoService.getMetrics(symbol.toUpperCase()).catch(() => null),
      fnoService.getSnapshots(symbol.toUpperCase(), 40).catch(() => [])
    ]).then(([metrics, snaps]) => {
      setFutMetrics(metrics);
      setFutSnapshots(snaps as FuturesSnapshot[]);
    }).finally(() => setFutLoading(false));
  }, [tab, symbol]);

  const handleAnalyze = async (direction: 'LONG' | 'SHORT') => {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const signal = await dispatch(analyzeSymbol({ symbol: symbol.toUpperCase(), direction })).unwrap();
      // Auto-load basket immediately so payoff graph / premium / ROI render without waiting for SignalCard
      if (signal?.basketId) {
        dispatch(fetchBasket(signal.basketId));
      }
    } catch (e: any) {
      setAnalysisError(e.message || 'Analysis failed — check that market data is fresh and try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await optionsService.refreshSymbol(symbol.toUpperCase());
      setLastRefreshedAt(new Date());
      // Reload lab data after a short delay to let backend poll complete
      setTimeout(() => {
        if (selectedExpiry) {
          dispatch(fetchLabData({ symbol: symbol.toUpperCase(), expiryId: Number(selectedExpiry), days: labDays > 0 ? labDays : 1 }));
        }
      }, 5000);
    } catch {
      // Silently ignore — refresh is best-effort
    } finally {
      setRefreshing(false);
    }
  };

  // Still waiting for expiries API
  if (!expiriesLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Expiries loaded but none exist — data not fetched yet (new symbol or first-time)
  if (expiriesLoaded && expiries.length === 0) {
    const handleFetchNow = async () => {
      setFetchingInitial(true);
      try {
        await optionsService.refreshSymbol(symbol.toUpperCase());
        // Poll for expiries every 4s for up to 60s
        let attempts = 0;
        const check = setInterval(async () => {
          attempts++;
          try {
            const res = await optionsService.getExpiries(symbol);
            const data = (res.data as any).data || [];
            if (data.length > 0) {
              clearInterval(check);
              setExpiries(data);
              setSelectedExpiry(data[0].id);
              setExpiriesLoaded(true);
              setFetchingInitial(false);
            } else if (attempts >= 15) {
              clearInterval(check);
              setFetchingInitial(false);
            }
          } catch { /* keep polling */ }
        }, 4000);
      } catch {
        setFetchingInitial(false);
      }
    };

    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/options-dashboard')} size="small" sx={{ mb: 2 }}>
          Dashboard
        </Button>
        <Alert severity="info" sx={{ maxWidth: 560 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            No option chain data yet for {symbol.toUpperCase()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Data is fetched from Zerodha and NSE — it's available any time, not just during market hours.
            If you just added this symbol, a fetch was triggered automatically. Click below to fetch now and
            the page will update as soon as data arrives.
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={fetchingInitial ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={handleFetchNow}
            disabled={fetchingInitial}
            sx={{ mt: 1.5 }}
          >
            {fetchingInitial ? 'Fetching data… (may take ~30s)' : 'Fetch Data Now'}
          </Button>
        </Alert>
      </Box>
    );
  }

  // Expiries loaded, waiting for lab data (lab===undefined means fetch in-flight)
  if (lab === undefined && selectedExpiry) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // lab===null means fetch failed
  if (lab === null) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/options-dashboard')} size="small" sx={{ mb: 2 }}>
          Dashboard
        </Button>
        <Alert severity="warning">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Option chain data not yet available for {symbol.toUpperCase()}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Data is collected from Zerodha. Ensure Zerodha is connected in Settings and try refreshing in a few minutes.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const chain = lab?.chain || [];
  const metrics = lab?.metrics || null;
  // metricsHistory — filtered by backend for the requested labDays period.
  // For multi-day views, downsample to ≤300 points so charts stay performant.
  const rawHistory: any[] = lab?.metricsHistory || [];
  const metricsHistory: any[] = (() => {
    const MAX_POINTS = 300;
    if (rawHistory.length <= MAX_POINTS) return rawHistory;
    const step = Math.ceil(rawHistory.length / MAX_POINTS);
    return rawHistory.filter((_, i) => i % step === 0);
  })();
  // Determine actual data range for user-facing info message
  const dataTrackingStart = metricsHistory.length > 0 ? new Date(metricsHistory[0].computedAt) : null;
  const actualDataDays = dataTrackingStart
    ? Math.ceil((Date.now() - dataTrackingStart.getTime()) / (24 * 60 * 60 * 1000))
    : 0;
  const showDataRangeInfo = labDays > 1 && actualDataDays < labDays && metricsHistory.length > 0;
  const spot = Number(metrics?.spotPrice || 0);
  const maxPain = Number(metrics?.maxPainStrike || 0);

  // Compute expiry context for expiry day warnings
  const selectedExpiryObj = expiries.find((e: any) => e.id === selectedExpiry);
  const expiryContext = selectedExpiryObj ? (() => {
    const today = new Date().toISOString().split('T')[0];
    const expiryDate = selectedExpiryObj.expiryDate;
    const dte = Math.round((new Date(expiryDate + 'T00:00:00').getTime() - new Date(today + 'T00:00:00').getTime()) / (24 * 60 * 60 * 1000));
    return { dte: Math.max(0, dte), isExpiryDay: dte === 0 };
  })() : null;

  // XAxis tick formatter: show time for intraday, date+time for multi-day.
  // Multi-day format: "15 Apr 10:30" — compact enough for minTickGap=60.
  const formatHistoryTick = (ts: string) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (labDays <= 1) {
      // Intraday: HH:MM only
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    if (labDays <= 7) {
      // Short multi-day: "15 Apr 10:30"
      const day = d.getDate().toString().padStart(2, '0');
      const mon = d.toLocaleString('en-IN', { month: 'short' });
      const hm  = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${day} ${mon} ${hm}`;
    }
    // Longer period: "15 Apr" — too many points for hour labels
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  return (
    <Box>
      {/* Phase C — Quick Basket FAB + Drawer */}
      <Box
        onClick={() => setBasketOpen(true)}
        sx={{
          position: 'fixed', right: 16, bottom: 16, zIndex: 1200,
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1, borderRadius: 4,
          bgcolor: C.accent, color: C.onSolid, boxShadow: 4, cursor: 'pointer',
          fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.3,
          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: 6 },
        }}
      >
        ⚡ Quick Basket
      </Box>
      <QuickBasketDrawer
        open={basketOpen}
        onClose={() => setBasketOpen(false)}
        symbol={symbol.toUpperCase()}
        spot={spot}
        chain={chain}
        lotSize={underlyingLot}
        expiryLabel={selectedExpiryObj?.expiryDate}
        feedSource={metrics?.feedSource}
      />

      <MarketStateStrip dataWarning={lab?.dataStale ? 'Data may be stale' : null} computedAt={metrics?.computedAt} feedSource={metrics?.feedSource} />

      {/* Expiry Day Warning Banner */}
      {expiryContext?.isExpiryDay && (
        <Alert severity="warning" sx={{ mt: 1, mb: 1, bgcolor: C.bearMuted, border: `1px solid ${C.bearBorder}`, '& .MuiAlert-icon': { color: C.bear } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.bear, mb: 0.5 }}>
            EXPIRY DAY — Special Caution Required
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Today is the expiry day for {selectedExpiryObj?.expiryDate} ({selectedExpiryObj?.expiryType}).
            Gamma is extreme. OTM options expire in hours. Straddle decay is rapid.
            Buying premium is very high risk. Max pain distance is the most important metric today.
          </Typography>
          {spot > 0 && maxPain > 0 && (
            <Typography variant="caption" sx={{ color: C.bear, fontWeight: 700, display: 'block', mt: 0.5 }}>
              Max Pain: ₹{maxPain.toLocaleString('en-IN')} | Spot: ₹{spot.toLocaleString('en-IN')} |
              Distance: {((Math.abs(spot - maxPain) / spot) * 100).toFixed(2)}%
              {Math.abs(spot - maxPain) / spot < 0.005 ? ' — STRONG PIN RISK' : ''}
            </Typography>
          )}
        </Alert>
      )}

      {/* Low DTE warning (1-2 days) */}
      {expiryContext && expiryContext.dte > 0 && expiryContext.dte <= 2 && (
        <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {expiryContext.dte} day(s) to expiry — theta decay is accelerating. Buying-premium strategies carry elevated time decay risk.
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/options-dashboard')} size="small">
          Dashboard
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>{symbol.toUpperCase()} Options Lab</Typography>
        <Button size="small" variant="contained" onClick={() => navigate(`/options?tab=trade&symbol=${encodeURIComponent(symbol)}`)}>Build a trade</Button>
        <FreshnessBadge computedAt={metrics?.computedAt} isExchangeOpen={isNseOpenFn()} feedSource={metrics?.feedSource} size="medium" />
        {spot > 0 && (
          <Chip label={`Spot: ₹${spot.toLocaleString('en-IN')}`}
            sx={{ bgcolor: C.warnMuted, color: C.warn, fontWeight: 700 }} />
        )}
        {metrics?.atmIv && (
          <Chip label={`ATM IV: ${Number(metrics.atmIv).toFixed(1)}%`}
            sx={{ bgcolor: `${ivColor(Number(metrics.ivRank52w) || 50)}22`, color: ivColor(Number(metrics.ivRank52w) || 50), fontWeight: 700 }} />
        )}
        {metrics?.ivRank52w != null && (
          <Chip label={`IVR 52w: ${Number(metrics.ivRank52w).toFixed(0)}`}
            sx={{ bgcolor: `${ivColor(Number(metrics.ivRank52w))}22`, color: ivColor(Number(metrics.ivRank52w)), fontWeight: 700 }} />
        )}

        {/* Expiry selector */}
        <FormControl size="small" sx={{ minWidth: 160, ml: 'auto' }}>
          <InputLabel>Expiry</InputLabel>
          <Select value={selectedExpiry} label="Expiry"
            onChange={(e) => setSelectedExpiry(Number(e.target.value))}>
            {expiries.map(e => (
              <MenuItem key={e.id} value={e.id}>
                {e.expiryDate} ({e.expiryType})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Lab Period selector — controls chart history window */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Lab Period</InputLabel>
          <Select value={labDays} label="Lab Period" onChange={e => handleLabDaysChange(Number(e.target.value))}>
            <MenuItem value={1}>1 Day</MenuItem>
            <MenuItem value={3}>3 Days</MenuItem>
            <MenuItem value={5}>5 Days</MenuItem>
            <MenuItem value={10}>10 Days</MenuItem>
            <MenuItem value={15}>15 Days</MenuItem>
            <MenuItem value={20}>20 Days</MenuItem>
            <MenuItem value={30}>30 Days</MenuItem>
          </Select>
        </FormControl>

        {/* Manual data refresh */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ color: C.accent, borderColor: C.accentBorder, minWidth: 100 }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh Data'}
          </Button>
          {metrics?.computedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>
              Last updated: {(() => {
                const mins = Math.floor((Date.now() - new Date(metrics.computedAt).getTime()) / 60000);
                return mins < 1 ? 'just now' : `${mins} min ago`;
              })()}
              {lastRefreshedAt && ` · refreshed ${Math.floor((Date.now() - lastRefreshedAt.getTime()) / 1000)}s ago`}
            </Typography>
          )}
        </Box>

        {/* Manual signal trigger */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined"
            disabled={analyzing}
            onClick={() => handleAnalyze('LONG')}
            sx={{ color: C.bull, borderColor: C.bullBorder }}>
            {analyzing ? <CircularProgress size={14} /> : 'Analyze LONG'}
          </Button>
          <Button size="small" variant="outlined"
            disabled={analyzing}
            onClick={() => handleAnalyze('SHORT')}
            sx={{ color: C.bear, borderColor: C.bearBorder }}>
            {analyzing ? <CircularProgress size={14} /> : 'Analyze SHORT'}
          </Button>
        </Box>
      </Box>

      {analysisError && <Alert severity="error" sx={{ mb: 2 }}>{analysisError}</Alert>}

      {/* Signal card (if active for this symbol) */}
      {activeSignal && (
        <Box sx={{ mb: 3 }}>
          <SignalCard
            signal={activeSignal}
            basketId={activeSignal.basketId ?? activeBasket?.id}
            onOpenLab={() => {}}
          />
        </Box>
      )}

      {/* Lab Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {['Overview', 'OI Analysis', 'IV & Skew', 'GEX', 'VWAP-OI', 'Option Chain', 'Futures'].map((label, i) => (
            <Tab key={label} label={label} id={`tab-${i}`}
              sx={{ fontWeight: 600, fontSize: '0.82rem', ...(label === 'Futures' ? { color: C.neutral } : {}) }} />
          ))}
        </Tabs>
      </Box>

      {/* Pinned metrics strip — visible across all tabs */}
      {metrics && (
        <Box sx={{
          display: 'flex', gap: 1, py: 1, px: 0.5, mb: 2, mt: 1,
          overflowX: 'auto', borderBottom: '1px solid', borderColor: 'divider',
          '&::-webkit-scrollbar': { height: 4 }
        }}>
          {[
            { label: 'ATM IV', value: metrics.atmIv ? `${Number(metrics.atmIv).toFixed(1)}%` : '—', color: ivColor(Number(metrics.ivRank52w) || 50) },
            { label: 'IVR 52w', value: metrics.ivRank52w != null ? String(Number(metrics.ivRank52w).toFixed(0)) : '—', color: ivColor(Number(metrics.ivRank52w)) },
            { label: 'PCR OI', value: metrics.pcrOi ? Number(metrics.pcrOi).toFixed(2) : '—', color: Number(metrics.pcrOi) < 0.8 ? C.bull : Number(metrics.pcrOi) > 1.2 ? C.bear : C.warn },
            { label: 'GEX', value: metrics.gexTotal ? `${(Number(metrics.gexTotal) / 1e6).toFixed(1)}M` : '—', color: Number(metrics.gexTotal) >= 0 ? C.bull : C.bear },
            { label: 'Max Pain', value: metrics.maxPainStrike ? `₹${Number(metrics.maxPainStrike).toLocaleString('en-IN')}` : '—', color: C.accent },
            { label: 'Straddle', value: metrics.straddlePrice ? `₹${Number(metrics.straddlePrice).toFixed(0)}` : '—', color: 'text.primary' },
          ].map(({ label, value, color }) => (
            <Box key={label} sx={{ flexShrink: 0, px: 1.5, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, minWidth: 72, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', display: 'block', letterSpacing: 0.3 }}>{label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: color as any }}>{value}</Typography>
            </Box>
          ))}

          {/* Trade-ready badge */}
          {(() => {
            const isDataFresh = metrics.computedAt && (Date.now() - new Date(metrics.computedAt).getTime()) < 5 * 60 * 1000;
            const isMarketOpen = isNseOpenFn();
            const isBtcSymbol = symbol.toUpperCase().includes('BTC');
            const ready = (isDataFresh || isBtcSymbol) && (isMarketOpen || isBtcSymbol);
            return (
              <Box sx={{
                ml: 'auto', flexShrink: 0, px: 1.5, py: 0.5, borderRadius: 1,
                bgcolor: ready ? C.bullMuted : C.bearMuted,
                border: '1px solid', borderColor: ready ? C.bullBorder : C.bearBorder,
                textAlign: 'center', minWidth: 100
              }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: ready ? C.bull : C.bear, letterSpacing: 0.5, display: 'block' }}>
                  {ready ? '● READY' : '○ NOT READY'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                  {!isDataFresh && !isBtcSymbol ? 'Data stale' : !isMarketOpen && !isBtcSymbol ? 'Market closed' : 'To trade'}
                </Typography>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Data range info — shown when user requests more history than exists */}
      {showDataRangeInfo && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          <Typography variant="caption">
            Tracking started {dataTrackingStart!.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.
            Charts show <strong>{actualDataDays} day{actualDataDays !== 1 ? 's' : ''}</strong> of available data
            (you selected {labDays} days — data will grow as the system collects more history).
          </Typography>
        </Alert>
      )}

      {/* Tab 0: Overview */}
      {tab === 0 && (
        <>
        <TabInfo title="How to read — Overview">
          <Note>• <b>Key Metrics grid:</b> ATM IV = current implied volatility of the at-the-money option. IVR (IV Rank) compares today's IV to its 52-week range — values below 30 are low (cheap options), above 65 are high (expensive options).</Note>
          <Note>• <b>HV vs IV chart:</b> When ATM IV is above HV 30d (Historical Volatility), options are overpriced relative to actual market movement — favour selling premium. When IV is below HV, options are cheap — favour buying.</Note>
          <Note>• <b>Payoff Curve:</b> Shows strategy profit/loss at expiry (solid line) vs today (dashed). The amber vertical line is the current spot price. Green zone = profit, red zone = loss. Break-even points are marked in purple.</Note>
          <Note>• <b>PCR OI:</b> Put-Call Ratio by Open Interest. Below 0.8 is bullish (more calls written), above 1.2 is bearish (more puts written), near 1.0 is neutral.</Note>
        </TabInfo>
        <Grid container spacing={3}>
          {activeSignal && (
            <Grid size={{ xs: 12 }}>
              <WhyThisSignalPanel signal={activeSignal} />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <OverviewMetrics metrics={metrics} onRefresh={handleRefresh} refreshing={refreshing} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {activeBasket && <PayoffCurve basket={activeBasket} spotPrice={spot} />}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <HVvsIVChart history={metricsHistory} tickFormatter={formatHistoryTick} />
          </Grid>
        </Grid>
        </>
      )}

      {/* Tab 1: OI Analysis */}
      {tab === 1 && (
        <>
        <TabInfo title="How to read — OI Analysis">
          <Note>• <b>OI Wall Map:</b> Dark red bars (↑) show Call OI = resistance levels where sellers are concentrated. Dark green bars (↓) show Put OI = support levels. The taller/darker the bar, the stronger the wall. Spot price (amber) and Max Pain (purple) are shown.</Note>
          <Note>• <b>PCR OI Trend:</b> Tracks Put-Call Ratio over time. Rising PCR = increasing put writing (bullish). Falling PCR = increasing call writing (bearish). The amber dashed line at 1.0 is the neutral level.</Note>
          <Note>• <b>OI Classification tags:</b> Bullish signals — PE Short Build (put sellers adding), CE Short Cover (call sellers exiting). Bearish signals — CE Short Build (call sellers adding), PE Long Build (put buyers adding). More strikes in a tag = stronger signal.</Note>
        </TabInfo>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <OIWallMap data={chain} spotPrice={spot} maxPainStrike={maxPain} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PCRChart history={metricsHistory} tickFormatter={formatHistoryTick} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OIClassificationCard oiClassification={metrics?.oiClassification} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <PCRInterpretation pcrOi={Number(metrics?.pcrOi || 0)} direction={activeSignal?.direction} />
          </Grid>
        </Grid>
        </>
      )}

      {/* Tab 2: IV & Skew */}
      {tab === 2 && (
        <>
        <TabInfo title="How to read — IV & Skew">
          <Note>• <b>IV Smile chart:</b> Shows CE IV (call implied volatility) and PE IV (put implied volatility) across strikes. The ATM strike (nearest to spot) is where the smile bottoms out. A steep smile means OTM options are expensive relative to ATM.</Note>
          <Note>• <b>Skew 25d:</b> Positive skew means OTM puts are more expensive than OTM calls (bearish fear premium). Negative skew is bullish. Skew above +5% indicates significant downside hedging activity.</Note>
          <Note>• <b>IV Rank (IVR):</b> Measures where current IV sits in its 52-week history. IVR &lt; 30 = IV is historically low, options are cheap — better for buying strategies. IVR &gt; 65 = IV is high — better for selling strategies.</Note>
          <Note>• <b>HV vs IV:</b> If IV &gt; HV 30d, options are overpriced vs actual movement — sellers have an edge. If IV &lt; HV, options are cheap — buyers have an edge.</Note>
        </TabInfo>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <IVSmileChart data={chain} spotPrice={spot} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <IVMetricsCard metrics={metrics} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <HVvsIVChart history={metricsHistory} tickFormatter={formatHistoryTick} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <GreeksHistoryChart
              history={metricsHistory}
              tickFormatter={formatHistoryTick}
              symbol={symbol.toUpperCase()}
              atmStrike={(() => {
                // Closest-to-spot strike from the chain
                if (!chain.length || !spot) return undefined;
                let best = chain[0].strike; let bestDist = Math.abs(best - spot);
                for (const r of chain) {
                  const d = Math.abs(r.strike - spot);
                  if (d < bestDist) { bestDist = d; best = r.strike; }
                }
                return best;
              })()}
              expirySeriesId={typeof selectedExpiry === 'number' ? selectedExpiry : undefined}
            />
          </Grid>
        </Grid>
        </>
      )}

      {/* Tab 3: GEX */}
      {tab === 3 && (
        <>
        <TabInfo title="How to read — GEX (Gamma Exposure)">
          <Note>• <b>GEX by Strike:</b> Gamma Exposure measures how much market makers need to hedge at each price level. Large positive GEX at a strike = market makers are long gamma there and will dampen volatility (acts as a magnet/pin). Large negative GEX = they are short gamma and will amplify moves.</Note>
          <Note>• <b>GEX Total:</b> Positive total GEX = overall pinning regime — expect rangebound price action near the largest positive bar. Negative total GEX = dealers are short gamma — expect larger, more volatile moves.</Note>
          <Note>• <b>GEX Flip Level:</b> The price at which total GEX flips from positive to negative. This is a critical level — breaking above/below it can trigger accelerated moves as dealers flip their hedging direction.</Note>
          <Note>• <b>GEX Trend chart:</b> Shows how GEX has changed over the selected period. Rapidly declining GEX heading negative signals a volatility expansion phase.</Note>
        </TabInfo>
        <GEXRegimeBanner gexTotal={Number(metrics?.gexTotal || 0)} gexByStrike={metrics?.gexByStrike || {}} gexFlipPrice={Number(metrics?.gexFlipPrice || 0)} spotPrice={spot} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <GEXChart
              gexByStrike={metrics?.gexByStrike || {}}
              gexFlipPrice={metrics?.gexFlipPrice}
              spotPrice={spot}
              gexTotal={metrics?.gexTotal}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <GEXTrendChart history={metricsHistory} tickFormatter={formatHistoryTick} />
          </Grid>
        </Grid>
        </>
      )}

      {/* Tab 4: VWAP-OI */}
      {tab === 4 && (
        <>
        <TabInfo title="How to read — VWAP-OI">
          <Note>• <b>CE VWAP-OI vs PE VWAP-OI:</b> Volume-Weighted Average Price of OI tracks where institutional players are positioned. CE VWAP-OI is the price at which call OI is concentrated; PE VWAP-OI is for puts.</Note>
          <Note>• <b>CE &gt; PE (CE Heavy):</b> More OI weighted towards calls — suggests institutional call writing (bearish expectation by large players). Market may face resistance near CE VWAP-OI level.</Note>
          <Note>• <b>PE &gt; CE (PE Heavy):</b> More OI weighted towards puts — suggests institutional put writing (bullish expectation). Market may find support near PE VWAP-OI level.</Note>
          <Note>• <b>Trend chart:</b> Watch for CE and PE VWAP-OI converging (indecision) or diverging (strong directional institutional bias). A sudden spike in either indicates fresh institutional positioning.</Note>
        </TabInfo>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <VwapOICard metrics={metrics} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <VwapOITrend history={metricsHistory} tickFormatter={formatHistoryTick} />
          </Grid>
        </Grid>
        </>
      )}

      {/* Tab 5: Option Chain */}
      {tab === 5 && (
        <>
        <TabInfo title="How to read — Option Chain">
          <Note>• <b>Strike highlighted in amber:</b> ATM (At-The-Money) strike — closest to current spot price. ATM options have the highest Theta decay and approximately 0.5 Delta.</Note>
          <Note>• <b>Strike highlighted in purple:</b> Max Pain strike — the price at which the most options (by value) expire worthless. Price tends to gravitate toward max pain near expiry.</Note>
          <Note>• <b>Delta:</b> Probability approximation — a Delta of 0.5 means ~50% chance of expiring ITM. For directional trades, use strikes with Delta 0.3–0.4.</Note>
          <Note>• <b>Theta:</b> Daily time decay in ₹. A Theta of -5 means the option loses ₹5 per day due to time decay alone. ATM options have the highest Theta.</Note>
          <Note>• <b>Gamma:</b> Rate of change of Delta. High Gamma near ATM = option price reacts strongly to spot moves. Positive for buyers, negative for sellers.</Note>
          <Note>• <b>Vega:</b> Sensitivity to IV changes. Long options have positive Vega (profit from IV rise). Short options have negative Vega (profit from IV fall).</Note>
          <Note>• <b>OI (Open Interest):</b> Total outstanding contracts. High OI at a strike = strong support (for PEs) or resistance (for CEs). Compare CE OI to PE OI to assess directional bias.</Note>
        </TabInfo>
        {chain.length > 0 ? (
          <OptionChainTable data={chain} spotPrice={spot} maxPainStrike={maxPain} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No chain data available. Wait for next poll cycle.
          </Typography>
        )}
        </>
      )}

      {/* Tab 6: Futures */}
      {tab === 6 && (
        <>
        <TabInfo title="How to read — Futures">
          <Note>• <b>FNO Confluence Score (0–100):</b> Combines 7 signals: OI Pattern (25%), Volume Surge (20%), Basis/Funding Rate (15%), Rollover Health (15%), Price-OI Correlation (10%), Delivery/COI (10%), Data Freshness (5%). Score ≥ 80 = strong signal, 65–79 = review, &lt;65 = weak.</Note>
          <Note>• <b>OI Pattern:</b> Long Build (OI ↑, Price ↑) = fresh longs bullish. Short Build (OI ↑, Price ↓) = fresh shorts bearish. Short Cover (OI ↓, Price ↑) = shorts exiting bullish. Long Unwind (OI ↓, Price ↓) = longs exiting bearish.</Note>
          <Note>• <b>Basis (Futures Price − Spot Price):</b> Positive basis = futures at premium (normal for bullish market). Negative basis = futures at discount (bearish / expiry effect). Large positive basis = strong bullish conviction.</Note>
          <Note>• <b>Roll Cost:</b> The cost to roll a futures position to the next expiry. High roll cost relative to basis = market expects continued direction; negative roll cost = market expects reversal.</Note>
          <Note>• <b>OI + Price chart:</b> Watch for divergence — rising price with falling OI = short covering (less durable move). Rising price with rising OI = fresh longs (durable bullish trend).</Note>
        </TabInfo>
        {futLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : !futMetrics ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No futures data yet for {symbol.toUpperCase()}. The Futures engine polls every 3 minutes — check back shortly,
            or ensure this symbol is in the tracked futures_underlyings list.
          </Alert>
        ) : (
          <FuturesLabTab metrics={futMetrics} snapshots={futSnapshots} symbol={symbol.toUpperCase()} />
        )}
        </>
      )}
    </Box>
  );
};

// ── Tab interpretation notes ──────────────────────────────────────────────────

const TabInfo: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { C } = useAppTheme();
  return (
    <Accordion disableGutters elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: C.neutral }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: C.neutral, letterSpacing: 0.4 }}>
            {title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.4, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const TabTakeaway: React.FC<{ text: string; color?: string }> = ({ text, color }) => {
  const { C } = useAppTheme();
  const c = color || C.neutral;
  return (
    <Box sx={{ p: 1, mb: 2, borderRadius: 1, bgcolor: C.neutralMuted, borderLeft: `3px solid ${c}` }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: c }}>
        TAKEAWAY: {text}
      </Typography>
    </Box>
  );
};

const PCRInterpretation: React.FC<{ pcrOi: number; direction?: string }> = ({ pcrOi, direction }) => {
  if (!pcrOi || pcrOi === 0) return null;
  const dominant = pcrOi > 1 ? 'Puts dominant' : 'Calls dominant';
  const contrarian = pcrOi > 1.3 ? 'BULLISH (crowded hedging — potential reversal)' :
                     pcrOi < 0.7 ? 'BEARISH (crowded call buying — potential reversal)' : 'NEUTRAL';
  const trendNote = pcrOi > 1.5 ? 'Extreme put positioning — contrarian bullish signal strengthens' :
                    pcrOi > 1.2 ? 'Rising PCR with puts being added — weakens contrarian effect if genuine bearish buying' :
                    pcrOi < 0.7 ? 'Very low PCR — heavy call positioning, contrarian bearish' : 'PCR near neutral';
  const directionNote = direction
    ? (direction === 'LONG'
        ? (pcrOi > 1.2 ? 'PCR supports contrarian LONG thesis (crowded puts)' : pcrOi < 0.8 ? 'PCR contradicts LONG — call-heavy market' : 'PCR neutral for LONG')
        : (pcrOi < 0.8 ? 'PCR supports contrarian SHORT thesis (crowded calls)' : pcrOi > 1.2 ? 'PCR contradicts SHORT — put-heavy market' : 'PCR neutral for SHORT'))
    : null;
  return (
    <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.3, display: 'block', mb: 0.5 }}>PCR INTERPRETATION</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>PCR OI: {pcrOi.toFixed(2)} — {dominant}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>Contrarian signal: {contrarian}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{trendNote}</Typography>
      {directionNote && <PCRDirectionNote note={directionNote} />}
    </Box>
  );
};

const PCRDirectionNote: React.FC<{ note: string }> = ({ note }) => {
  const { C } = useAppTheme();
  return <Typography variant="caption" sx={{ color: C.neutral, fontWeight: 700, display: 'block', mt: 0.5 }}>{note}</Typography>;
};

const GEXRegimeBanner: React.FC<{ gexTotal: number; gexByStrike: Record<string, number>; gexFlipPrice: number; spotPrice: number }> = ({
  gexTotal, gexByStrike, gexFlipPrice, spotPrice
}) => {
  const { C } = useAppTheme();
  if (!gexTotal || !gexByStrike) return null;
  const strikes = Object.entries(gexByStrike);
  const negativeCount = strikes.filter(([, v]) => Number(v) < 0).length;
  const negPct = strikes.length > 0 ? (negativeCount / strikes.length) * 100 : 0;
  const isAmplified = gexTotal < 0 && negPct > 70;
  if (!isAmplified) return null;
  const flipDistance = gexFlipPrice && spotPrice > 0 ? ((gexFlipPrice - spotPrice) / spotPrice * 100).toFixed(2) : null;
  return (
    <Alert severity="warning" sx={{ mb: 2, bgcolor: C.bearMuted, border: `1px solid ${C.bearBorder}`, '& .MuiAlert-icon': { color: C.bear } }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.bear, mb: 0.5 }}>AMPLIFIED VOLATILITY REGIME</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {negPct.toFixed(0)}% of strikes show negative gamma (GEX Total: {(gexTotal / 1e6).toFixed(1)}M).
        Dealers are short gamma — they will BUY when price falls and SELL when price rises, amplifying moves in both directions.
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        Strategy Impact: Range-bound strategies are riskier. Directional structures with defined risk preferred. Avoid selling naked straddles/strangles.
      </Typography>
      {gexFlipPrice > 0 && spotPrice > 0 && (
        <Typography variant="caption" sx={{ color: C.accent, fontWeight: 700, display: 'block', mt: 0.5 }}>
          GEX Flip Level: ₹{gexFlipPrice.toLocaleString('en-IN')} ({flipDistance}% from spot)
        </Typography>
      )}
    </Alert>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const OverviewMetrics: React.FC<{ metrics: any; onRefresh?: () => void; refreshing?: boolean }> = ({ metrics: m, onRefresh, refreshing }) => {
  const { C } = useAppTheme();
  // No metrics for the selected expiry yet — actionable empty state instead of a
  // perpetual "Loading…" (the latter hung when an expiry had no captured snapshot).
  if (!m) {
    return (
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          {refreshing ? 'Fetching latest option data…' : 'No option metrics captured for this expiry yet. Click Refresh Data, or pick a nearer expiry.'}
        </Typography>
        {onRefresh && (
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh Data'}
          </Button>
        )}
      </Box>
    );
  }
  const rows = [
    { label: 'ATM IV', value: `${Number(m.atmIv || 0).toFixed(2)}%`, color: undefined },
    { label: 'IVR 52w', value: `${Number(m.ivRank52w || 0).toFixed(0)}`, color: undefined },
    { label: 'IVR 30d', value: `${Number(m.ivRank30d || 0).toFixed(0)}`, color: undefined },
    { label: 'HV 30d', value: `${Number(m.hv30d || 0).toFixed(2)}%`, color: undefined },
    { label: 'PCR Volume', value: Number(m.pcr || 0).toFixed(2), color: undefined },
    { label: 'PCR OI', value: Number(m.pcrOi || 0).toFixed(2), color: undefined },
    { label: 'Max Pain', value: m.maxPainStrike ? `₹${Number(m.maxPainStrike).toLocaleString('en-IN')}` : '—', color: C.accent },
    { label: 'Straddle', value: m.straddlePrice ? `₹${Number(m.straddlePrice).toFixed(0)}` : '—', color: undefined },
    { label: 'GEX Total', value: m.gexTotal ? `${(Number(m.gexTotal) / 1e6).toFixed(1)}M` : '—',
      color: Number(m.gexTotal || 0) >= 0 ? C.bull : C.bear },
    { label: 'GEX Flip', value: m.gexFlipPrice ? `₹${Number(m.gexFlipPrice).toLocaleString('en-IN')}` : '—', color: C.accent },
    { label: 'Skew 25d', value: m.skew25d ? `${Number(m.skew25d).toFixed(2)}%` : '—', color: undefined },
    { label: 'CE VWAP-OI', value: m.ceVwapOi ? `₹${Number(m.ceVwapOi).toFixed(1)}` : '—', color: C.callText },
    { label: 'PE VWAP-OI', value: m.peVwapOi ? `₹${Number(m.peVwapOi).toFixed(1)}` : '—', color: C.putText }
  ];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Key Metrics</Typography>
      <Grid container spacing={1}>
        {rows.map(({ label, value, color }) => (
          <Grid size={{ xs: 6, sm: 4 }} key={label}>
            <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
        Computed: {m.computedAt ? new Date(m.computedAt).toLocaleString('en-IN') : '—'}
      </Typography>
    </Box>
  );
};

const PCRChart: React.FC<{ history: any[]; tickFormatter?: (ts: string) => string }> = ({ history, tickFormatter }) => {
  const { C } = useAppTheme();
  const data = history.map(h => ({
    ts: h.computedAt,
    'PCR OI': Number(h.pcrOi || 0).toFixed(2)
  }));
  if (!data.length) return null;
  const fmt = tickFormatter || ((ts: string) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const fmtFull = (ts: string) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>PCR OI Trend</Typography>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="ts" tickFormatter={fmt} tick={{ fontSize: 9 }} minTickGap={60} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip labelFormatter={fmtFull} formatter={(v: any) => [v, 'PCR OI']}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
          <ReferenceLine y={1} stroke={C.warn} strokeDasharray="4 4" label={{ value: '1.0', fill: C.warn, fontSize: 9 }} />
          <Line dataKey="PCR OI" stroke={C.chartLine1} dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

const OIClassificationCard: React.FC<{ oiClassification?: Record<string, any[]> }> = ({ oiClassification }) => {
  const { C } = useAppTheme();
  if (!oiClassification) return null;
  const bullish = ['PE_SHORT_BUILD', 'CE_SHORT_COVER', 'PE_LONG_UNWIND'];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>OI Classification</Typography>
      {Object.entries(oiClassification).filter(([, v]) => v.length > 0).map(([label, items]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Chip size="small" label={label}
            sx={{ bgcolor: bullish.includes(label) ? C.bullMuted : C.bearMuted,
              color: bullish.includes(label) ? C.bull : C.bear,
              fontSize: '0.65rem', fontWeight: 700 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{items.length} strikes</Typography>
        </Box>
      ))}
    </Box>
  );
};

const IVMetricsCard: React.FC<{ metrics: any }> = ({ metrics: m }) => {
  if (!m) return null;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>IV Summary</Typography>
      {[
        ['ATM IV', `${Number(m.atmIv || 0).toFixed(2)}%`],
        ['IVR 52w', `${Number(m.ivRank52w || 0).toFixed(0)}`],
        ['IVR 30d', `${Number(m.ivRank30d || 0).toFixed(0)}`],
        ['HV 30d', `${Number(m.hv30d || 0).toFixed(2)}%`],
        ['IV Premium', m.atmIv && m.hv30d ? `${(Number(m.atmIv) - Number(m.hv30d)).toFixed(2)}%` : '—'],
        ['Skew 25d', m.skew25d ? `${Number(m.skew25d).toFixed(2)}%` : '—']
      ].map(([l, v]) => (
        <Box key={l as string} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{l}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>{v}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const GEXTrendChart: React.FC<{ history: any[]; tickFormatter?: (ts: string) => string }> = ({ history, tickFormatter }) => {
  const { C } = useAppTheme();
  const data = history.map(h => ({
    ts: h.computedAt,
    GEX: h.gexTotal ? (Number(h.gexTotal) / 1e6).toFixed(2) : null
  }));
  if (!data.length) return null;
  const fmt = tickFormatter || ((ts: string) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const fmtFull = (ts: string) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>GEX Trend</Typography>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="ts" tickFormatter={fmt} tick={{ fontSize: 9 }} minTickGap={60} />
          <YAxis tickFormatter={v => `${v}M`} tick={{ fontSize: 10 }} />
          <Tooltip labelFormatter={fmtFull} formatter={(v: any) => [`${v}M`, 'GEX']}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
          <ReferenceLine y={0} stroke={C.warn} strokeDasharray="4 4" label={{ value: '0', fill: C.warn, fontSize: 9 }} />
          <Line dataKey="GEX" stroke={C.accent} dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

const VwapOICard: React.FC<{ metrics: any }> = ({ metrics: m }) => {
  const { C } = useAppTheme();
  if (!m) return null;
  const ceVwap = Number(m.ceVwapOi || 0);
  const peVwap = Number(m.peVwapOi || 0);
  const institutional = ceVwap > 0 && peVwap > 0 ? (ceVwap > peVwap ? 'CE Heavy (Bearish Flow)' : 'PE Heavy (Bullish Flow)') : '—';
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>VWAP-OI Institutional Flow</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: C.callBg, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: C.callText, fontWeight: 700, display: 'block' }}>CE VWAP-OI</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: C.callText }}>₹{ceVwap.toFixed(1)}</Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1.5, bgcolor: C.putBg, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: C.putText, fontWeight: 700, display: 'block' }}>PE VWAP-OI</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: C.putText }}>₹{peVwap.toFixed(1)}</Typography>
        </Box>
      </Box>
      <Chip label={institutional}
        sx={{ bgcolor: ceVwap > peVwap ? C.bearMuted : C.bullMuted,
          color: ceVwap > peVwap ? C.bear : C.bull, fontWeight: 700 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
        CE VWAP-OI &gt; PE = institutional writing calls (bearish). PE &gt; CE = writing puts (bullish).
      </Typography>
    </Box>
  );
};

const VwapOITrend: React.FC<{ history: any[]; tickFormatter?: (ts: string) => string }> = ({ history, tickFormatter }) => {
  const { C } = useAppTheme();
  const data = history.map(h => ({
    ts: h.computedAt,
    'CE VWAP': h.ceVwapOi ? Number(h.ceVwapOi).toFixed(1) : null,
    'PE VWAP': h.peVwapOi ? Number(h.peVwapOi).toFixed(1) : null
  }));
  if (!data.length) return null;
  const fmt = tickFormatter || ((ts: string) => new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const fmtFull = (ts: string) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }) : '';
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>VWAP-OI Trend</Typography>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="ts" tickFormatter={fmt} tick={{ fontSize: 9 }} minTickGap={60} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip labelFormatter={fmtFull}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line dataKey="CE VWAP" stroke={C.callText} dot={false} strokeWidth={2} connectNulls />
          <Line dataKey="PE VWAP" stroke={C.putText} dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ── Futures Lab Tab ───────────────────────────────────────────────────────────

const COMPONENT_META = [
  { key: 'oiPattern',   label: 'OI Pattern',          weight: 25, color: '#4f9cf7' },
  { key: 'volumeSurge', label: 'Volume Surge',         weight: 20, color: '#a78bfa' },
  { key: 'basis',       label: 'Basis / Funding Rate', weight: 15, color: '#2dd4aa' },
  { key: 'rollover',    label: 'Rollover Health',      weight: 15, color: '#f5a623' },
  { key: 'correlation', label: 'Price-OI Correlation', weight: 10, color: '#f5793a' },
  { key: 'delivery',    label: 'Delivery / COI',       weight: 10, color: '#4f9cf7' },
  { key: 'freshness',   label: 'Data Freshness',       weight:  5, color: '#2dd4aa' },
];

const OI_PATTERN_LABEL_TEXT: Record<string, string> = {
  LONG_BUILD:  'Long Build ↑ — Fresh longs, Bullish',
  SHORT_COVER: 'Short Cover ↑ — Shorts squeezing, Bullish',
  SHORT_BUILD: 'Short Build ↓ — Fresh shorts, Bearish',
  LONG_UNWIND: 'Long Unwind ↓ — Longs exiting, Bearish',
  NEUTRAL:     'Neutral — No clear directional bias',
};

function oiPatternMeta(pattern: string, C: AppColorTokens) {
  const isBull = pattern === 'LONG_BUILD' || pattern === 'SHORT_COVER';
  const isBear = pattern === 'SHORT_BUILD' || pattern === 'LONG_UNWIND';
  return {
    label: OI_PATTERN_LABEL_TEXT[pattern] || OI_PATTERN_LABEL_TEXT.NEUTRAL,
    color: isBull ? C.bull : isBear ? C.bear : C.dim
  };
}

const FuturesLabTab: React.FC<{
  metrics: FuturesDerivedMetrics;
  snapshots: FuturesSnapshot[];
  symbol: string;
}> = ({ metrics: m, snapshots, symbol }) => {
  const { C } = useAppTheme();
  const score = Math.round(Number(m.futuresScore || 0));
  const scoreColor = score >= 80 ? C.bull : score >= 65 ? C.warn : C.bear;
  const regime = score >= 80 ? 'STRONG' : score >= 65 ? 'REVIEW' : 'REJECT';
  const breakdown = (m.scoreBreakdown || {}) as Record<string, any>;
  const patternMeta = oiPatternMeta(m.oiPattern || 'NEUTRAL', C);
  const isCrypto = symbol.endsWith('USD') || symbol.endsWith('USDT') || symbol.endsWith('USDC');

  // Chart data from snapshots (already in chronological order)
  const chartData = snapshots.map(s => ({
    time: new Date(s.capturedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
    'Futures Price': s.futuresPrice ? Number(Number(s.futuresPrice).toFixed(2)) : null,
    'Spot Price':    s.spotPrice    ? Number(Number(s.spotPrice).toFixed(2))    : null,
    'OI (K)':        s.openInterest ? Number((Number(s.openInterest) / 1000).toFixed(1)) : null,
    'Volume (K)':    s.volume       ? Number((Number(s.volume) / 1000).toFixed(1))       : 0,
    'Basis':         s.basis        ? Number(Number(s.basis).toFixed(2))                 : null,
    'Funding%':      s.fundingRate  ? Number((Number(s.fundingRate) * 100).toFixed(4))   : null,
  }));

  return (
    <Box>
      <Grid container spacing={3}>

        {/* ── Score Card ────────────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${scoreColor}44`, bgcolor: `${scoreColor}08` }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: scoreColor }}>{regime}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Futures Confluence Score</Typography>
            </Box>

            {/* OI Pattern */}
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: `${patternMeta.color}12`, border: `1px solid ${patternMeta.color}33`, mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>OI Pattern</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: patternMeta.color, fontSize: '0.8rem' }}>
                {patternMeta.label}
              </Typography>
            </Box>

            {/* Key values */}
            <Grid container spacing={1}>
              {[
                { label: 'Futures',   value: m.futuresPrice ? Number(m.futuresPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—' },
                { label: 'Spot',      value: m.spotPrice    ? Number(m.spotPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 })    : '—' },
                { label: isCrypto ? 'Funding' : 'Basis',
                  value: isCrypto
                    ? (m.basisOrFunding != null ? `${(Number(m.basisOrFunding)*100).toFixed(4)}%` : '—')
                    : (m.basis != null ? Number(m.basis).toFixed(2) : '—') },
                { label: 'OI',        value: m.openInterest ? `${(Number(m.openInterest)/1000).toFixed(0)}K` : '—' },
                { label: 'Rollover',  value: m.rolloverPct  != null ? `${Number(m.rolloverPct).toFixed(1)}%`  : '—' },
                { label: 'Correl r',  value: m.priceOiCorrelation != null ? Number(m.priceOiCorrelation).toFixed(3) : '—' },
              ].map(({ label, value }) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Box sx={{ p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              Computed: {new Date(m.computedAt).toLocaleString('en-IN')}
            </Typography>
          </Box>
        </Grid>

        {/* ── Score Components ───────────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Score Components (0–100 each)
            </Typography>
            {COMPONENT_META.map(({ key, label, weight, color }) => {
              const comp = breakdown[key] || {};
              const s = Number(comp.score ?? 0);
              const bar = s >= 65 ? C.bull : s >= 40 ? C.warn : C.bear;
              return (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{label}</Typography>
                      <Chip size="small" label={`${weight}%`}
                        sx={{ height: 16, fontSize: '0.6rem', bgcolor: `${color}22`, color }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {comp.label && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                          {comp.label}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 800, color: bar, minWidth: 30, textAlign: 'right' }}>
                        {Math.round(s)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ position: 'relative', height: 8, borderRadius: 1, bgcolor: `${bar}22`, overflow: 'hidden' }}>
                    <Box sx={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${s}%`, borderRadius: 1,
                      bgcolor: bar, transition: 'width 0.6s ease'
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, mt: 0.3 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                      Weighted contribution: {(s * weight / 100).toFixed(1)} pts
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Grid>

        {/* ── OI + Price Chart ───────────────────────────────────────────── */}
        {chartData.length > 1 && (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Futures Price vs Open Interest — {chartData.length} snapshots
              </Typography>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis yAxisId="price" tick={{ fontSize: 10 }} tickFormatter={v => v.toLocaleString('en-IN')} />
                  <YAxis yAxisId="oi" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${v}K`} />
                  <Tooltip formatter={(v: any, name: string) => [
                    name === 'OI (K)' ? `${v}K` : v?.toLocaleString?.('en-IN') ?? v, name
                  ]} />
                  <Legend />
                  <Area yAxisId="oi" type="monotone" dataKey="OI (K)" fill={C.neutralMuted} stroke={`${C.neutral}55`} dot={false} />
                  <Line yAxisId="price" type="monotone" dataKey="Futures Price" stroke={C.warn} dot={false} strokeWidth={2} />
                  <Line yAxisId="price" type="monotone" dataKey="Spot Price" stroke={C.dim} dot={false} strokeWidth={1} strokeDasharray="4 2" />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}

        {/* ── Volume Chart ───────────────────────────────────────────────── */}
        {chartData.length > 1 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Volume Surge (K contracts)
                {m.volumeSurgeRatio != null && (
                  <Chip size="small" label={`${Number(m.volumeSurgeRatio).toFixed(2)}× avg`}
                    sx={{ ml: 1, bgcolor: Number(m.volumeSurgeRatio) >= 1.5 ? C.bullMuted : C.warnMuted,
                      color: Number(m.volumeSurgeRatio) >= 1.5 ? C.bull : C.warn, fontWeight: 700, height: 18 }} />
                )}
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}K`} />
                  <Tooltip formatter={(v: any) => [`${v}K`, 'Volume']} />
                  <Bar dataKey="Volume (K)" fill="#a78bfa88" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}

        {/* ── Basis / Funding Chart ─────────────────────────────────────── */}
        {chartData.length > 1 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                {isCrypto ? 'Funding Rate (%)' : 'Basis (Futures − Spot)'}
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
                  <Area type="monotone"
                    dataKey={isCrypto ? 'Funding%' : 'Basis'}
                    fill="#2dd4aa22" stroke="#2dd4aa" dot={false} strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default OptionsLabPage;
